/// <reference types="node" />
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Document, DocumentStatus } from '../../types/document.ts';
import { Search, Plus, Filter, Send, X, UserCircle, Users, GitBranch } from 'lucide-react';
import { useRedirect, useNotify } from 'react-admin';
import axios from 'axios';
import { submitDocumentForApproval, getDocumentWorkflowInstance, markDocumentReviewNoChange, deleteDocument } from '../../services/documentService.ts';
import { fetchUserPermissions } from '../../services/permissionService.ts';
import { PERMISSION_SNAPSHOT_KEY } from '../../components/DynamicMenu.tsx';
import { PERMISSION_SNAPSHOT_UPDATED_EVENT } from '../../services/permissionGuard.ts';
import { getAllUsers } from '../../services/userService.ts';
import { useActionPermissionAlwaysRemote as useActionPermission } from '../../hooks/useActionPermissionAlwaysRemote.ts';

interface Approver {
  id: string;
  name: string;
  role: string;
  email: string;
  userId?: string;
  departmentTitle?: string;
  departmentTitleId?: string;
  selectorType?: 'user' | 'departmentTitle';
  selectorValue?: string;
}

interface ApprovalStep {
  id: string;
  stepNumber: number;
  stepName: string;
  stepType: 'single' | 'parallel' | 'conditional';
  approvers: Approver[];
  approvalRule: 'all' | 'any' | 'majority';
  timeLimit?: number;
  description: string;
  isOptional: boolean;
}

// Map backend numeric status to string status
const mapStatusToString = (status: number | string): DocumentStatus => {
  if (typeof status === 'string') return status as DocumentStatus;
  const statusMap: Record<number, DocumentStatus> = {
    0: 'Draft',
    1: 'Review',
    2: 'Approved',
    3: 'Obsolete'
  };
  return statusMap[status] || 'Draft';
};

const toSearchableText = (value: unknown): string => String(value ?? '').toLowerCase();

const normalizeTags = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(tag => String(tag ?? '').trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value.split(',').map(tag => tag.trim()).filter(Boolean);
  }
  return [];
};

const normalizeDocumentFromApi = (raw: any): Document => {
  const documentNumber =
    raw?.documentNumber ?? raw?.docNumber ?? raw?.docNum ?? raw?.DocumentNumber ?? raw?.DocNumber ?? raw?.DocNum ?? '';

  return {
    ...(raw as Document),
    id: String(raw?.id ?? raw?.Id ?? ''),
    documentNumber: String(documentNumber ?? ''),
    title: String(raw?.title ?? raw?.Title ?? ''),
    description: String(raw?.description ?? raw?.Description ?? ''),
    category: String(raw?.category ?? raw?.Category ?? ''),
    currentVersion: String(raw?.currentVersion ?? raw?.version ?? raw?.Version ?? '1.0'),
    ownerId: raw?.ownerId != null ? String(raw.ownerId) : raw?.owner != null ? String(raw.owner) : undefined,
    ownerName: raw?.ownerName != null ? String(raw.ownerName) : raw?.ownerUserName != null ? String(raw.ownerUserName) : undefined,
    tags: normalizeTags(raw?.tags ?? raw?.Tags),
    status: mapStatusToString(raw?.status),
    nextReviewDate:
      raw?.nextReviewAt ??
      raw?.nextReviewDate ??
      raw?.nextReview ??
      raw?.NextReviewAt ??
      raw?.NextReviewDate ??
      undefined,
  };
};

const normalizeWorkflowInstanceSteps = (workflowInstance: any): { steps: ApprovalStep[]; templateInstanceId?: number } => {
  const rawSteps = Array.isArray(workflowInstance)
    ? workflowInstance
    : (workflowInstance?.steps || []);

  const templateInstanceIdRaw = Array.isArray(workflowInstance)
    ? (workflowInstance[0]?.instanceId ?? workflowInstance[0]?.templateInstanceId ?? workflowInstance[0]?.templateId)
    : (workflowInstance?.templateInstanceId ?? workflowInstance?.instanceId ?? workflowInstance?.templateId);

  const steps: ApprovalStep[] = rawSteps.map((step: any, index: number) => {
    const approvers: Approver[] = [
      ...(step.approvers || []).map((approver: any) => ({
        id: String(approver.userId ?? approver.id ?? approver.userName ?? `user-${index}`),
        userId: String(approver.userId ?? approver.id ?? ''),
        name: String(approver.userName ?? approver.name ?? ''),
        role: String(approver.roleName ?? approver.role ?? 'User'),
        email: String(approver.email ?? ''),
        selectorType: 'user',
        selectorValue: String(approver.userId ?? approver.id ?? ''),
      })),
      ...(step.departmentTitles || []).map((dept: any) => ({
        id: `dept-${dept.departmentTitleId ?? dept.id ?? index}`,
        name: String(dept.departmentTitle ?? dept.title ?? ''),
        role: 'DepartmentTitle',
        email: '',
        departmentTitle: String(dept.departmentTitle ?? dept.title ?? ''),
        departmentTitleId: String(dept.departmentTitleId ?? dept.id ?? ''),
        selectorType: 'departmentTitle',
        selectorValue: String(dept.departmentTitleId ?? dept.id ?? dept.departmentTitle ?? dept.title ?? ''),
      })),
    ];

    return {
      id: String(step.id ?? step.stepId ?? `step-${index + 1}`),
      stepNumber: Number(step.stepNo ?? step.stepNumber ?? step.level ?? index + 1),
      stepName: String(step.name ?? step.stepName ?? `Step ${step.stepNo ?? step.stepNumber ?? index + 1}`),
      stepType: (step.stepType || 'single') as ApprovalStep['stepType'],
      approvers,
      approvalRule: (step.approvalRule || 'all') as ApprovalStep['approvalRule'],
      timeLimit: step.timeLimit,
      description: String(step.description ?? ''),
      isOptional: step.isOptional === true,
    };
  });

  return {
    steps,
    templateInstanceId: templateInstanceIdRaw != null && String(templateInstanceIdRaw).trim() !== ''
      ? Number(templateInstanceIdRaw)
      : undefined,
  };
};

const DOC_TYPE_OPTIONS = ['SOP', 'QMS', 'Forms', 'Policy', 'SDS'];

export default function DocumentManageList() {
  const redirect = useRedirect();
  const notify = useNotify();
  const { can, canAny, isPermissionReady, actions, scopedActions } = useActionPermission();
  const userId = localStorage.getItem('userId');
  console.log('[权限][页面] userId:', userId, 'actions:', actions, 'scopedActions:', scopedActions);
  const testCanSubmit = canAny(['documentslist:submit', 'documentslist:call'], { menu: 'documentsList', allowGlobalFallback: false });
  console.log('[权限][页面] canAny([submit, call]) =', testCanSubmit);

  const canCreateDocument = can('documentslist:create', { menu: 'documentsList', allowGlobalFallback: false });
  const canEditDocument = can('documentslist:edit', { menu: 'documentsList', allowGlobalFallback: false });
  const canDeleteDocument = can('documentslist:delete', { menu: 'documentsList', allowGlobalFallback: false });
  const canOverwriteDocument = can('documentslist:overwrite', { menu: 'documentsList', allowGlobalFallback: false });
  const canSubmitDocument = can('documentslist:submit', { menu: 'documentsList', allowGlobalFallback: false });
  // 兼容部分环境把预览权限配置为 view
  const canPreviewDocument = canAny(['documentslist:preview', 'documentslist:view'], { menu: 'documentsList', allowGlobalFallback: false });

  // Define navigation functions
  const onNewDocument = () => {
    if (!canCreateDocument) {
      notify('No permission to create document', { type: 'warning' });
      return;
    }
    // First parameter is 'create', second parameter must match the Resource name property, which is 'documentsList'
    redirect('create', 'documentsList');
  };

  const onEditDocument = (id: string) => {
    if (!canEditDocument) {
      notify('No permission to edit document', { type: 'warning' });
      return;
    }
    // First parameter is 'edit', second parameter must match the Resource name property, which is 'documentsList'
    redirect('edit', 'documentsList', id);
  };


const onViewDocument = (id: string) => {
  redirect('show', 'documentsList', id);
};

  const onDeleteDocument = (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!canDeleteDocument) {
      notify('No permission to delete document', { type: 'warning' });
      return;
    }

    if (String(doc.status).trim().toLowerCase() !== 'draft') {
      notify('Only Draft documents can be deleted', { type: 'warning' });
      return;
    }

    setDeletingDoc(doc);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingDoc) return;

    setDeleting(true);
    try {
      await deleteDocument(deletingDoc.id);
      notify('Document deleted successfully!', { type: 'success' });
      setShowDeleteModal(false);
      setDeletingDoc(null);
      setRefreshToken((value) => value + 1);
    } catch (error: any) {
      console.error('Failed to delete document:', error);
      notify(`Failed to delete document: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  


  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | 'All'>('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [ownerFilter, setOwnerFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [approvalSteps, setApprovalSteps] = useState<ApprovalStep[]>([]);
  const [approversPool, setApproversPool] = useState<Approver[]>([]);
  const [selectedTemplateInstanceId, setSelectedTemplateInstanceId] = useState<number | undefined>(undefined);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewingDocId, setReviewingDocId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState<Document | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  // Ensure modals are closed on mount (prevents leftover overlay after navigation/login)
  useEffect(() => {
    setShowSubmitModal(false);
    setSelectedDocId(null);
    setSelectedTemplateInstanceId(undefined);
    setWorkflowLoading(false);
    setApprovalSteps([]);
    setShowDeleteModal(false);
    setDeletingDoc(null);
  }, []);

  const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
  const API_URL = `${BASE_API_URL}/QmsDocument`;

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      setError(null);
      try {
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize - 1;
        const range = `[${start},${end}]`;
        const filter: Record<string, string> = {};

        if (categoryFilter !== 'All') {
          filter.category = categoryFilter;
        }

        if (statusFilter !== 'All') {
          filter.status = statusFilter;
        }

        const response = await axios.get<any>(API_URL, {
          params: {
            range,
            perPage: pageSize,
            filter: Object.keys(filter).length ? JSON.stringify(filter) : undefined,
          },
        });
        // Map numeric status to string status
        const documentsWithMappedStatus = (response.data || []).map((doc: any) => normalizeDocumentFromApi(doc));
        setDocuments(documentsWithMappedStatus);
        
        // Get total count from response headers if available
        const contentRange = response.headers['content-range'];
        if (contentRange) {
          const match = contentRange.match(/\d+-\d+\/(\d+)/);
          if (match) {
            setTotalCount(parseInt(match[1], 10));
          }
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load documents, please try again later');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [API_URL, categoryFilter, statusFilter, currentPage, pageSize, refreshToken]);

  // 简单且直接：组件挂载时立即拉取后端权限并写入 permissionSnapshot，确保页面上能立即使用 action 权限
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      setPermissionLoading(false);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        setPermissionLoading(true);
        const perms = await fetchUserPermissions(userId);
        if (!mounted) return;
        const snapshot = {
          menuIds: (perms.menus || []).map((m: any) => m.id),
          actions: perms.actionCodes || [],
          scopedActions: perms.scopedActionCodes || [],
          timestamp: Date.now(),
        };
        try {
          localStorage.setItem(PERMISSION_SNAPSHOT_KEY, JSON.stringify(snapshot));
          window.dispatchEvent(new Event(PERMISSION_SNAPSHOT_UPDATED_EVENT));
        } catch (e) {
          // ignore storage errors
        }
      } catch (err) {
        // ignore
      } finally {
        if (mounted) setPermissionLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load approvers
  useEffect(() => {
    const loadApprovers = async () => {
      try {
        const users = await getAllUsers();
        const approvers: Approver[] = users
          .map((user: any) => {
            const resolvedId = user.userId ?? user.id ?? user.Id;
            if (resolvedId === undefined || resolvedId === null || String(resolvedId).trim() === '') {
              return null;
            }
            return {
              id: String(resolvedId),
              name: user.userName || user.username || user.name || '',
              role: user.roleName || user.role || 'User',
              email: user.email || '',
            };
          })
          .filter((item: Approver | null): item is Approver => item !== null);
        setApproversPool(approvers);
      } catch (error) {
        console.error('Failed to load approvers:', error);
      }
    };
    loadApprovers();
  }, []);

  // Load workflow instance when submit modal opens
  useEffect(() => {
    if (!showSubmitModal || !selectedDocId) return;

    let mounted = true;
    const loadWorkflowInstance = async () => {
      try {
        setWorkflowLoading(true);
        setApprovalSteps([]);
        setSelectedTemplateInstanceId(undefined);

        const workflowInstance = await getDocumentWorkflowInstance(selectedDocId);
        if (!mounted) return;

        if (!workflowInstance) {
          setApprovalSteps([]);
          setSelectedTemplateInstanceId(undefined);
          return;
        }

        const normalized = normalizeWorkflowInstanceSteps(workflowInstance);
        setApprovalSteps(normalized.steps);
        setSelectedTemplateInstanceId(normalized.templateInstanceId);
      } catch (error) {
        console.error('Failed to load workflow instance:', error);
        if (mounted) {
          setApprovalSteps([]);
          setSelectedTemplateInstanceId(undefined);
        }
      } finally {
        if (mounted) setWorkflowLoading(false);
      }
    };

    loadWorkflowInstance();
    return () => {
      mounted = false;
    };
  }, [showSubmitModal, selectedDocId]);

  const categories = useMemo(() => ['All', ...DOC_TYPE_OPTIONS], []);

  const userNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    approversPool.forEach(user => {
      map[user.id] = user.name;
    });
    return map;
  }, [approversPool]);

  const ownerOptions = useMemo(() => {
    const normalized = approversPool
      .map(user => ({ id: String(user.id), name: user.name || String(user.id) }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [{ id: 'All', name: 'All Owners' }, ...normalized];
  }, [approversPool]);

  const getOwnerId = useCallback((doc: Document) => {
    const ownerId = (doc as any).ownerId ?? (doc as any).owner;
    if (ownerId == null || ownerId === '') return '';
    return String(ownerId);
  }, []);

  const getOwnerDisplayName = useCallback((doc: Document) => {
    if (doc.ownerName) return doc.ownerName;
    const ownerId = (doc as any).ownerId ?? (doc as any).owner;
    if (ownerId == null || ownerId === '') return '-';
    return userNameMap[String(ownerId)] || String(ownerId);
  }, [userNameMap]);

  const filteredDocuments = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return documents.filter(doc => {
      const ownerName = getOwnerDisplayName(doc);
      const ownerId = getOwnerId(doc);
      const tags = Array.isArray((doc as any).tags) ? (doc as any).tags : normalizeTags((doc as any).tags);

      const matchesSearch =
        normalizedQuery.length === 0 ||
        toSearchableText(doc.title).includes(normalizedQuery) ||
        toSearchableText((doc as any).documentNumber ?? (doc as any).docNumber ?? (doc as any).docNum).includes(normalizedQuery) ||
        toSearchableText(doc.description).includes(normalizedQuery) ||
        tags.some((tag: unknown) => toSearchableText(tag).includes(normalizedQuery)) ||
        toSearchableText(ownerName).includes(normalizedQuery);

      const matchesOwner = ownerFilter === 'All' || ownerId === ownerFilter;

      return matchesSearch && matchesOwner;
    });
  }, [documents, searchQuery, ownerFilter, getOwnerDisplayName, getOwnerId]);

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Reset to page 1 when any filter changes (so the range starts from first page)
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, categoryFilter, ownerFilter]);

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case 'Draft':
        return 'bg-gray-100 text-gray-700';
      case 'Review':
        return 'bg-yellow-100 text-yellow-700';
      case 'Approved':
        return 'bg-green-100 text-green-700';
      case 'Obsolete':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const handlePreview = (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (!doc.draftFileUrl) {
        notify('Document URL not available', { type: 'warning' });
        return;
      }
      window.open(doc.draftFileUrl, '_blank', 'noopener,noreferrer');
    } catch (error: any) {
      console.error('Failed to preview document:', error);
      notify(`Preview failed: ${error?.message || 'Unknown error'}`, { type: 'error' });
    }
  };

  const handleSubmitForApproval = async (docId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canSubmitDocument) {
      notify('No permission to submit for approval', { type: 'warning' });
      return;
    }
    setSelectedDocId(docId);
    setApprovalSteps([]);
    setSelectedTemplateInstanceId(undefined);
    setWorkflowLoading(true);
    setShowSubmitModal(true);
  };

  const handleReview = async (docId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setReviewingDocId(docId);
    setShowReviewModal(true);
  };

  const handleReviewNoChange = async () => {
    if (!reviewingDocId) return;
    
    try {
      const currentUserId = localStorage.getItem('userId') || '1000';
      await markDocumentReviewNoChange(reviewingDocId, currentUserId);
      notify('Review date updated successfully', { type: 'success' });
      
      // Refresh list
      const response = await axios.get<any>(API_URL);
      const documentsWithMappedStatus = (response.data || []).map((doc: any) => normalizeDocumentFromApi(doc));
      setDocuments(documentsWithMappedStatus);
      
      setShowReviewModal(false);
      setReviewingDocId(null);
    } catch (error: any) {
      console.error('Failed to update review date:', error);
      notify(`Failed to update review date: ${error.message || 'Unknown error'}`, { type: 'error' });
    }
  };

  const handleReviewWithChanges = () => {
    if (!reviewingDocId) return;
    setShowReviewModal(false);
    // Redirect to dedicated revision page
    redirect(`/documentsList/${reviewingDocId}/revision`);
    setReviewingDocId(null);
  };

  const getStepTypeBadge = (type: string) => {
    const configs: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      single: {
        label: 'Single',
        color: 'bg-blue-100 text-blue-800',
        icon: <UserCircle className="size-3" />
      },
      parallel: {
        label: 'Parallel',
        color: 'bg-purple-100 text-purple-800',
        icon: <Users className="size-3" />
      },
      conditional: {
        label: 'Conditional',
        color: 'bg-orange-100 text-orange-800',
        icon: <GitBranch className="size-3" />
      }
    };
    const config = configs[type] || configs.single;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const handleConfirmSubmit = async () => {
    if (!selectedDocId) return;

    setSubmitting(true);
    try {
      const result = await submitDocumentForApproval(
        selectedDocId,
        selectedTemplateInstanceId,
        approvalSteps.length > 0 ? approvalSteps : undefined
      );
      
      notify(`Document submitted successfully! Workflow Instance ID: ${result.id}, Current Step: ${result.currentStepNo}`, { type: 'success' });
      setShowSubmitModal(false);
      setSelectedDocId(null);
      setApprovalSteps([]);
      setSelectedTemplateInstanceId(undefined);
      
      // Refresh document list without redirect
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/QmsDocument`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Map numeric status to string status
      const documentsWithMappedStatus = (response.data || []).map((doc: any) => normalizeDocumentFromApi(doc));
      setDocuments(documentsWithMappedStatus);
    } catch (error: any) {
      console.error('Failed to submit document:', error);
      notify(`Failed to submit document: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isPermissionReady || permissionLoading) {
    return (
      <div className="w-full h-full overflow-auto flex flex-col bg-transparent">
        <div className="flex-1 px-2 pt-2 pb-4 overflow-auto">
          <div className="min-h-full rounded-2xl border border-gray-200 bg-white shadow-[0_6px_18px_rgba(15,23,42,0.05)] overflow-hidden">
            <div className="p-10 flex items-center justify-center text-slate-600 text-sm">
              Loading ...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-auto flex flex-col bg-transparent">
      <div className="flex-1 px-2 pt-2 pb-4 overflow-auto">
        <div className="min-h-full rounded-2xl border border-gray-200 bg-white shadow-[0_6px_18px_rgba(15,23,42,0.05)] overflow-hidden">
          {/* Header + Filters (merged into same card) */}
          <div className="p-4 pb-3">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div>
                <h2 className="text-[22px] font-extrabold text-slate-900 leading-none">Document Management</h2>
                <p className="text-sm text-slate-500 mt-1">Manage document lifecycle, versioning, and approvals.</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center h-8 px-3 rounded-full bg-slate-100 text-slate-700 text-sm font-bold">
                  Total Documents: {loading ? '...' : totalCount}
                </span>
                {canCreateDocument && (
                  <button
                    type="button"
                    onClick={onNewDocument}
                    className="inline-flex items-center gap-2 px-4 h-9 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold"
                  >
                    <Plus className="w-4 h-4" />
                    New Document
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2.5">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by document number, title, description or tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600 text-sm">Filter:</span>
                </div>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as DocumentStatus | 'All')}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="All">All Status</option>
                  <option value="Draft">Draft</option>
                  <option value="Review">In Review</option>
                  <option value="Approved">Approved</option>
                  <option value="Obsolete">Obsolete</option>
                </select>

                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat === 'All' ? 'All Categories' : cat}
                    </option>
                  ))}
                </select>

                <select
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  {ownerOptions.map(owner => (
                    <option key={owner.id} value={owner.id}>
                      {owner.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100" />

          {error && (
            <div className="px-6 py-4 text-red-600">{error}</div>
          )}

          <div style={{ margin: 10, borderRadius: '14px', overflow: 'hidden' }}>
            <table className="w-full border-collapse table-fixed ">
            <thead className="bg-slate-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-3 text-left text-slate-700 font-bold text-sm min-w-[80px] w-[160px]">Title</th>
                <th className="px-2 py-3 text-left text-slate-700 font-bold whitespace-nowrap text-sm min-w-[80px] w-[88px]">DocNum</th>
                <th className="px-1.5 py-3 text-left text-slate-700 font-bold whitespace-nowrap text-sm min-w-[80px] w-[80px]">Category</th>
                <th className="px-1.5 py-3 text-left text-slate-700 font-bold whitespace-nowrap text-sm min-w-[80px] w-[80px]">Owner</th>
                <th className="px-1.5 py-3 text-center text-slate-700 font-bold whitespace-nowrap text-sm min-w-[80px] w-[80px]">Version</th>
                <th className="px-1.5 py-3 text-center text-slate-700 font-bold whitespace-nowrap text-sm min-w-[80px] w-[80px]">Status</th>
                <th className="px-1.5 py-3 text-center text-slate-700 font-bold whitespace-nowrap text-sm min-w-[80px] w-[100px]">Next Review</th>
                <th className="px-3 py-3 text-center text-slate-700 font-bold whitespace-nowrap text-sm min-w-[320px]">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {filteredDocuments.map(doc => (
                <tr
                  key={doc.id}
                  className="hover:bg-slate-50 even:bg-[#fbfdff] transition-colors cursor-pointer text-sm"
                  onClick={() => onViewDocument(doc.id)}
                >
                  {/* Title */}
                  <td className="px-3 py-2.5">
                    <div className="text-gray-900 font-medium truncate text-sm">{doc.title}</div>
                  </td>

                  {/* Document Number */}
                  <td className="px-3 py-2.5 text-blue-700 font-semibold whitespace-nowrap text-sm">{doc.documentNumber}</td>


                  {/* Category */}
                  <td className="px-1.5 py-2.5">
                    <span className="inline-block px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs whitespace-nowrap">
                      {doc.category}
                    </span>
                  </td>

                  {/* Owner */}
                  <td className="px-1.5 py-2.5 text-sm text-gray-700 truncate" title={getOwnerDisplayName(doc)}>
                    {getOwnerDisplayName(doc)}
                  </td>

                  {/* Version */}
                  <td className="px-1.5 py-2.5 text-center text-gray-900 italic whitespace-nowrap text-sm">{doc.currentVersion}</td>

                  {/* Status */}
                  <td className="px-2 py-2.5 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded font-base text-xs whitespace-nowrap ${getStatusColor(doc.status)}`}>
                      {doc.status === 'Draft' && 'Draft'}
                      {doc.status === 'Review' && 'In Review'}
                      {doc.status === 'Approved' && 'Approved'}
                      {doc.status === 'Obsolete' && 'Obsolete'}
                    </span>
                  </td>

                  {/* Next Review */}
                  <td className="px-1.5 py-2.5 text-center text-gray-700 whitespace-nowrap text-sm">
                    {doc.nextReviewDate ? new Date(doc.nextReviewDate).toLocaleDateString('en-GB') : '-'}
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-2.5 align-top">
                    {(() => {
                      const isApproved = String(doc.status).trim().toLowerCase() === 'approved';
                      return (
                    <div className="grid grid-cols-3 gap-1.5 justify-items-stretch">
                      <button
                        onClick={(e) => { e.stopPropagation(); onViewDocument(doc.id); }}
                        className="inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md transition-colors"
                        title="View"
                      >
                        View
                      </button>
                      {canEditDocument && doc.status !== 'Obsolete' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onEditDocument(doc.id); }}
                          disabled={isApproved}
                          className={`inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold border rounded-md transition-colors ${
                            isApproved
                              ? 'text-gray-400 border-gray-200 bg-gray-100 cursor-not-allowed'
                              : 'text-slate-700 border-slate-200 hover:bg-slate-100'
                          }`}
                          title={isApproved ? 'Approved documents cannot be edited' : 'Edit'}
                        >
                          Edit
                        </button>
                      )}
                      {canSubmitDocument && (
                        <button
                          onClick={(e) => handleSubmitForApproval(doc.id, e)}
                          disabled={String(doc.status).trim().toLowerCase() !== 'draft'}
                          className={`inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold rounded-md border transition-colors ${
                            String(doc.status).trim().toLowerCase() === 'draft'
                              ? 'text-emerald-700 border-emerald-200 hover:bg-emerald-50'
                              : 'text-gray-400 border-gray-200 bg-gray-100 cursor-not-allowed'
                          }`}
                          title={String(doc.status).trim().toLowerCase() === 'draft' ? 'Submit' : 'Already submitted'}
                        >
                          Submit
                        </button>
                      )}
                      {canPreviewDocument && (
                        <button
                          onClick={(e) => handlePreview(doc, e)}
                          className="inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-100 rounded-md transition-colors"
                          title="Preview"
                        >
                          Preview
                        </button>
                      )}
                      {canDeleteDocument && String(doc.status).trim().toLowerCase() === 'draft' && (
                        <button
                          onClick={(e) => onDeleteDocument(doc, e)}
                          className="inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold text-red-700 border border-red-200 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete"
                        >
                          Delete
                        </button>
                      )}
                      {doc.status === 'Approved' && (
                        <button
                          onClick={(e) => handleReview(doc.id, e)}
                          className="inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md transition-colors"
                          title="Review/Revision"
                        >
                          Review/Revision
                        </button>
                      )}
                      {canOverwriteDocument && doc.status === 'Approved' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); redirect(`/documentsList/${doc.id}/edit?mode=overwrite`); }}
                          className="inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold text-amber-700 border border-amber-200 hover:bg-amber-50 rounded-md transition-colors"
                          title="Overwrite"
                        >
                          Overwrite
                        </button>
                      )}
                    </div>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>

          {/* Empty state message */}
          {filteredDocuments.length === 0 && (
            <div className="py-12 text-center text-gray-500">
              No documents found
            </div>
          )}

          {showDeleteModal && deletingDoc && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-md w-full">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900">Delete Document</h3>
                </div>
                <div className="p-6">
                  <div className="mb-4">
                    <p className="text-gray-900 font-medium mb-1">Are you sure you want to delete this document?</p>
                    <p className="text-sm text-gray-600">This action cannot be undone.</p>
                    <div className="bg-gray-50 rounded-lg p-3 mt-3 space-y-1">
                      <div className="text-sm"><span className="font-medium">Title:</span> {deletingDoc.title}</div>
                      <div className="text-sm"><span className="font-medium">Number:</span> {deletingDoc.documentNumber}</div>
                      <div className="text-sm"><span className="font-medium">Status:</span> <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">{deletingDoc.status}</span></div>
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeletingDoc(null);
                    }}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          {documents.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-slate-50">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <span>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value, 10));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <span>
                  {totalCount === 0 ? 'No data' : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)} of ${totalCount}`}
                </span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || loading}
                  className="p-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Previous Page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <div className="flex items-center h-8 px-2 mx-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                    // Show page numbers with smart logic: show 5 pages around current
                    const showPages = Math.min(5, totalPages);
                    const startPage = Math.max(1, Math.min(currentPage - Math.floor(showPages / 2), totalPages - showPages + 1));
                    const endPage = Math.min(totalPages, startPage + showPages - 1);
                    
                    if (pageNum < startPage || pageNum > endPage) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`min-w-8 h-8 px-0.5 rounded text-sm transition-colors ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages || loading}
                  className="p-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Next Page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Submit for Approval Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full my-4 max-h-[calc(100vh-2rem)] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Submit for Approval</h3>
                  <p className="text-sm text-gray-500 mt-1">Workflow instance preview only. Read-only.</p>
                </div>
                <button
                  onClick={() => setShowSubmitModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={submitting || workflowLoading}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <div className="font-semibold text-slate-900">Loaded from workflow instance</div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span>Document ID: {selectedDocId || '-'}</span>
                  <span>Instance ID: {selectedTemplateInstanceId ?? '-'}</span>
                  <span>{workflowLoading ? 'Loading instance...' : `${approvalSteps.length} step(s)`}</span>
                </div>
              </div>

              {workflowLoading ? (
                <div className="py-10 text-center text-slate-500">Loading workflow instance...</div>
              ) : approvalSteps.length > 0 ? (
                <div className="space-y-3">
                  {approvalSteps.map((step, index) => (
                    <div key={step.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {step.stepNumber}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h4 className="font-medium text-gray-900">{step.stepName}</h4>
                            {getStepTypeBadge(step.stepType)}
                            {step.isOptional && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">Optional</span>
                            )}
                            {step.timeLimit && (
                              <span className="text-xs text-gray-500">{step.timeLimit} day limit</span>
                            )}
                          </div>

                          {step.description && (
                            <p className="text-sm text-gray-600 mb-3">{step.description}</p>
                          )}

                          <div className="space-y-3">
                            {step.approvers.some(a => a.selectorType === 'departmentTitle') && (
                              <div>
                                <div className="text-xs font-semibold text-purple-600 mb-1.5 uppercase tracking-wide">Department Titles</div>
                                <div className="flex flex-wrap gap-2">
                                  {step.approvers.filter(a => a.selectorType === 'departmentTitle').map(approver => (
                                    <div
                                      key={approver.id}
                                      className="inline-flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-sm"
                                    >
                                      <UserCircle className="size-4 text-purple-500 flex-shrink-0" />
                                      <div>
                                        <div className="font-medium text-gray-900 text-sm">{approver.departmentTitle || approver.name}</div>
                                        <div className="text-xs text-purple-600">Dynamic by Department Title</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {step.approvers.some(a => a.selectorType !== 'departmentTitle') && (
                              <div>
                                <div className="text-xs font-semibold text-blue-600 mb-1.5 uppercase tracking-wide">Persons</div>
                                <div className="flex flex-wrap gap-2">
                                  {step.approvers.filter(a => a.selectorType !== 'departmentTitle').map(approver => (
                                    <div
                                      key={approver.id}
                                      className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm"
                                    >
                                      <UserCircle className="size-4 text-blue-500 flex-shrink-0" />
                                      <div>
                                        <div className="font-medium text-gray-900 text-sm">{approver.name}</div>
                                        <div className="text-xs text-gray-500">{approver.departmentTitle || approver.role}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                  <GitBranch className="size-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-2">No workflow instance found for this document</p>
                  <p className="text-sm text-gray-500">This submit view is read-only and depends on the document's current instance.</p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => setShowSubmitModal(false)}
                disabled={submitting || workflowLoading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                disabled={submitting || workflowLoading || approvalSteps.length === 0}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review/Revision Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Document Review/Revision</h3>
              <p className="text-sm text-gray-600 mt-1">Has the document content changed? Choose Review or Revision.</p>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-gray-700">
                Choose what to do with this document review/revision:
              </p>
              <div className="space-y-2">
                <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="font-medium text-gray-900 text-sm">Review</div>
                  <div className="text-xs text-gray-600 mt-1">Only update the next review date (2 years from now)</div>
                </div>
                <div className="p-3 border border-gray-200 rounded-lg bg-blue-50">
                  <div className="font-medium text-gray-900 text-sm">Revision</div>
                  <div className="text-xs text-gray-600 mt-1">Create a new revision with updated content</div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => setShowReviewModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReviewNoChange}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                No Changes
              </button>
              <button
                type="button"
                onClick={handleReviewWithChanges}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Has Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
